// components/TestRunner/rules/Rule02_FiberOccupied_NoReport.js

/**
 * Rule02_FiberOccupied_NoReport.js
 *
 * ✅ Règle 02 — Une fibre déclarée occupée ne doit avoir aucun PV associé
 * Type: Error
 *
 * 🧪 Tests effectués :
 * • Vérification de l'existence : fiber.etat, hasDcoPvReussite, hasPvReussite, hasDcoPvEchec, hasPvEchec, hasPvHs
 * • Contrôle _isFiberOccupied(fiber)
 * • Détection PV Réussite : hasDcoPvReussite=true OU hasPvReussite=true
 * • Détection PV Échec : hasDcoPvEchec=true OU hasPvEchec=true
 * • Détection PV Hors Service : hasPvHs=true
 * • Détection combinaisons multiples de PV
 */

export class Rule02_FiberOccupied_NoReport {
  /**
   * Appliquer la règle 02 : Aucun PV pour fibre occupée
   * @param {Object} fiber - Objet fibre avec toutes ses données
   */
  static apply(fiber) {
    // Cas 1 : Vérifier l'existence des données nécessaires
    const missingDataError = this._checkMissingData(fiber);
    if (missingDataError) {
      this._addError(fiber, "R02", missingDataError.title, missingDataError.message);
      return;
    }

    // Vérifier si la fibre est occupée
    if (!this._isFiberOccupied(fiber)) {
      return; // Règle ne s'applique qu'aux fibres occupées
    }

    // Analyser la présence de PV (qui ne devrait pas exister)
    const pvAnalysis = this._analyzePVPresence(fiber);

    if (pvAnalysis.hasError) {
      this._addError(fiber, "R02", pvAnalysis.errorTitle, pvAnalysis.errorMessage);
    }
  }

  /**
   * Vérifier l'existence des données nécessaires
   * @param {Object} fiber - Objet fibre
   * @returns {Object|null} Erreur ou null si OK
   */
  static _checkMissingData(fiber) {
    const requiredFields = [
      { field: "etat", label: "État de la fibre" },
      { field: "hasDcoPvReussite", label: "Fibre dans le Rapport FiberCable PV Succès" },
      { field: "hasPvReussite", label: "Fichiers de mesures présents pour un PV Succès" },
      { field: "hasDcoPvEchec", label: "Fibre dans le Rapport FiberCable PV Échec" },
      { field: "hasPvEchec", label: "Fichiers de mesures présents pour un PV Échec" },
      { field: "hasPvHs", label: "Fichiers de mesures présents pour un PV Hors Service" },
    ];

    // Vérifier si au moins un champ est manquant
    const missingFields = requiredFields.filter(
      (item) => fiber[item.field] === undefined || fiber[item.field] === null
    );

    if (missingFields.length > 0) {
      return {
        title: "Impossible d'exécuter le test - Données de configuration manquantes",
        message: this._generateMissingDataMessage(fiber, requiredFields),
      };
    }

    return null;
  }

  /**
   * Analyser la présence de PV pour une fibre occupée
   * @param {Object} fiber - Objet fibre
   * @returns {Object} Résultat de l'analyse
   */
  static _analyzePVPresence(fiber) {
    const analysis = {
      hasError: false,
      errorTitle: "",
      errorMessage: "",
    };

    // Vérifier les différents types de PV
    const hasSuccessPV = fiber.hasDcoPvReussite === true || fiber.hasPvReussite === true;
    const hasFailurePV = fiber.hasDcoPvEchec === true || fiber.hasPvEchec === true;
    const hasOutOfServicePV = fiber.hasPvHs === true;

    // Compter les types de PV présents
    const pvTypes = [hasSuccessPV, hasFailurePV, hasOutOfServicePV].filter(Boolean).length;

    // Cas 2d : Plusieurs types de PV
    if (pvTypes > 1) {
      analysis.hasError = true;
      analysis.errorTitle = "Fibre occupée avec plusieurs types de PV détectés";
      analysis.errorMessage = this._generateMultiplePVMessage(fiber);
      return analysis;
    }

    // Cas 2a : PV Réussite
    if (hasSuccessPV) {
      // Verifier si ce n'est pas un gain confirmé
      // Vérifier si ce n'est PAS un cas de "Gain confirmé"
      const hasGainConfirme =
        fiber.hasConflictResolutions &&
        fiber.conflictResolutions.COMMENT &&
        fiber.conflictResolutions.COMMENT.toLowerCase().includes("gain confirmé");
      if (!hasGainConfirme) {
        // Seulement si pas de "Gain confirmé" -> ERREUR
        analysis.hasError = true;
        analysis.errorTitle = "Fibre occupée avec PV Réussite détecté";
        analysis.errorMessage = this._generateSuccessPVMessage(fiber);
        return analysis;
      }
      // Si "Gain confirmé" -> pas d'erreur, on continue
      return analysis; // analysis.hasError reste false
    }

    // Cas 2b : PV Échec
    if (hasFailurePV) {
      analysis.hasError = true;
      analysis.errorTitle = "Fibre occupée avec PV Échec détecté";
      analysis.errorMessage = this._generateFailurePVMessage(fiber);
      return analysis;
    }

    // Cas 2c : PV Hors Service
    if (hasOutOfServicePV) {
      analysis.hasError = true;
      analysis.errorTitle = "Fibre occupée avec PV Hors Service détecté";
      analysis.errorMessage = this._generateOutOfServicePVMessage(fiber);
      return analysis;
    }

    return analysis;
  }

  /**
   * Vérifier si une fibre est occupée
   * @param {Object} fiber - Objet fibre
   * @returns {boolean} true si la fibre est occupée
   */
  static _isFiberOccupied(fiber) {
    if (!fiber.etat) return false;

    const etatLower = fiber.etat.toLowerCase().trim();
    return etatLower === "occupee";
  }

  /**
   * Générer le message pour données manquantes
   * @param {Object} fiber - Objet fibre
   * @param {Array} requiredFields - Liste des champs requis
   * @returns {string} Message HTML formaté
   */
  static _generateMissingDataMessage(fiber, requiredFields) {
    let message = `<strong>⚠️ Problème détecté :</strong><br>`;
    message += `Impossible d'exécuter le test - Données de configuration manquantes<br><br>`;

    message += `<strong>Détail des flags manquants :</strong><br>`;
    requiredFields.forEach((item) => {
      const value = fiber[item.field];
      const status = value !== undefined && value !== null ? "✅" : "❌";
      message += `- ${item.label} : ${status}<br>`;
    });

    message += `<br><strong>⚡ Action requise :</strong><br>`;
    message += `- Vérifier la déclaration de la fibre dans la Configuration des Tiroirs<br>`;
    message += `- Vérifier la présence dans un rapport fiber cable<br>`;
    message += `- Vérifier la présence de fichiers de mesures PV Réussite, PV Échec, PV Hors Service`;

    return message;
  }

  /**
   * Générer le message pour PV Réussite détecté
   * @param {Object} fiber - Objet fibre
   * @returns {string} Message HTML formaté
   */
  static _generateSuccessPVMessage(fiber) {
    let message = `<strong>⚠️ Problème détecté :</strong><br>`;
    message += `Fibre occupée avec PV Réussite détecté<br><br>`;

    message += `<strong>Détail du problème :</strong><br>`;
    message += `- Fibre dans le Rapport FiberCable PV Succès : ${fiber.hasDcoPvReussite ? "✅" : "❌"}<br>`;
    message += `- Fichiers de mesures présents pour un PV Succès : ${fiber.hasPvReussite ? "✅" : "❌"}<br><br>`;

    message += `<strong>⚡ Action requise :</strong><br>`;
    message += `- Supprimer les PV Réussite associés à cette fibre occupée<br>`;
    message += `- Vérifier l'état réel de la fibre sur le terrain`;

    return message;
  }

  /**
   * Générer le message pour PV Échec détecté
   * @param {Object} fiber - Objet fibre
   * @returns {string} Message HTML formaté
   */
  static _generateFailurePVMessage(fiber) {
    let message = `<strong>⚠️ Problème détecté :</strong><br>`;
    message += `Fibre occupée avec PV Échec détecté<br><br>`;

    message += `<strong>Détail du problème :</strong><br>`;
    message += `- Fibre dans le Rapport FiberCable PV Échec : ${fiber.hasDcoPvEchec ? "✅" : "❌"}<br>`;
    message += `- Fichiers de mesures présents pour un PV Échec : ${fiber.hasPvEchec ? "✅" : "❌"}<br><br>`;

    message += `<strong>⚡ Action requise :</strong><br>`;
    message += `- Supprimer les PV Échec associés à cette fibre occupée<br>`;
    message += `- Vérifier l'état réel de la fibre sur le terrain`;

    return message;
  }

  /**
   * Générer le message pour PV Hors Service détecté
   * @param {Object} fiber - Objet fibre
   * @returns {string} Message HTML formaté
   */
  static _generateOutOfServicePVMessage(fiber) {
    let message = `<strong>⚠️ Problème détecté :</strong><br>`;
    message += `Fibre occupée avec PV Hors Service détecté<br><br>`;

    message += `<strong>Détail du problème :</strong><br>`;
    message += `- Fichiers de mesures présents pour un PV Hors Service : ${fiber.hasPvHs ? "✅" : "❌"}<br><br>`;

    message += `<strong>⚡ Action requise :</strong><br>`;
    message += `- Supprimer le PV Hors Service associé à cette fibre occupée<br>`;
    message += `- Vérifier l'état réel de la fibre sur le terrain`;

    return message;
  }

  /**
   * Générer le message pour plusieurs types de PV
   * @param {Object} fiber - Objet fibre
   * @returns {string} Message HTML formaté
   */
  static _generateMultiplePVMessage(fiber) {
    let message = `<strong>⚠️ Problème détecté :</strong><br>`;
    message += `Fibre occupée avec plusieurs types de PV détectés<br><br>`;

    message += `<strong>Détail du problème :</strong><br>`;
    message += `- Fibre dans le Rapport FiberCable PV Succès : ${fiber.hasDcoPvReussite ? "✅" : "❌"}<br>`;
    message += `- Fichiers de mesures présents pour un PV Succès : ${fiber.hasPvReussite ? "✅" : "❌"}<br>`;
    message += `- Fibre dans le Rapport FiberCable PV Échec : ${fiber.hasDcoPvEchec ? "✅" : "❌"}<br>`;
    message += `- Fichiers de mesures présents pour un PV Échec : ${fiber.hasPvEchec ? "✅" : "❌"}<br>`;
    message += `- Fichiers de mesures présents pour un PV Hors Service : ${fiber.hasPvHs ? "✅" : "❌"}<br><br>`;

    message += `<strong>⚡ Action requise :</strong><br>`;
    message += `- Supprimer tous les PV associés à cette fibre occupée<br>`;
    message += `- Vérifier l'état réel de la fibre sur le terrain`;

    return message;
  }

  /**
   * Ajouter une erreur au résultat d'une fibre
   */
  static _addError(fiber, rule, title, message) {
    // Initialize testResults if it doesn't exist
    if (!fiber.testResults) {
      fiber.testResults = {
        status: "success",
        hasError: false,
        messages: [],
      };
    }

    // Configure error details
    fiber.testResults.status = "error";
    fiber.testResults.hasError = true;
    fiber.testResults.messages.push({
      type: "error",
      rule,
      title,
      message,
    });
  }
}
